<template>
  <v-navigation-drawer
    v-model="state.open"
    right
    temporary
    app
    class="lapsed-pay-plan-drawer-connected"
    width="480"
  >
    <div v-if="state.loading" class="lppdc__loading" data-testid="lppdc-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <div v-else-if="state.error" class="lppdc__error" data-testid="lppdc-inline-error" role="alert">
      {{ state.error }}
    </div>

    <LapsedPayPlanDrawer
      v-else-if="version"
      :lapsed-reason="lapsedReason"
      :version="version"
      :fair-pay-at-effective-date="fairPayAtEffectiveDate"
      :voidable="voidable"
      :void-blocked-by="voidBlockedBy"
      @void="openVoid"
      @open-chatter="emit('open-chatter', { id: state.versionId, versionCode: version?.code })"
      @close="close"
    />

    <!-- Void confirmation (irreversible; reason gates confirm — Rule B-14) -->
    <PayPlanDialog
      :open="state.voidOpen"
      title="Void pay plan version"
      :submitting="state.submitting"
      @close="cancelVoid"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 6.667v3.333m0 3.333h.008M8.575 2.283 1.517 14.167a1.667 1.667 0 0 0 1.425 2.5h14.116a1.667 1.667 0 0 0 1.425-2.5L11.425 2.283a1.667 1.667 0 0 0-2.85 0Z"
            stroke="#d92d20"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        This action is irreversible. Voiding
        <strong>{{ version?.code || "this version" }}</strong> permanently voids this pay plan
        version. A reason is required.
      </template>

      <label class="lppdc__field-label" for="lppdc-void-reason">Reason</label>
      <v-textarea
        id="lppdc-void-reason"
        v-model="state.voidReason"
        data-testid="lppdc-void-reason"
        rows="3"
        auto-grow
        hide-details
        outlined
        dense
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          data-testid="lppdc-void-cancel"
          :disabled="state.submitting"
          @click="cancelVoid"
        />
        <StandardButton
          type="red"
          variant="primary"
          size="normal"
          label="Void"
          data-testid="lppdc-void-confirm"
          :disabled="!state.voidReason.trim() || state.submitting || featureDisabled"
          :loader-left="state.submitting"
          @click="voidVersion"
        />
      </template>
    </PayPlanDialog>
  </v-navigation-drawer>
</template>

<script setup>
import StandardButton from "@components/StandardButton/StandardButton.vue";
import PayPlanDialog from "@components/PayPlanDialog/PayPlanDialog.vue";
import LapsedPayPlanDrawer from "@components/LapsedPayPlanDrawer/LapsedPayPlanDrawer.vue";
import { useLapsedPayPlanDrawer } from "./composables/useLapsedPayPlanDrawer";

const props = defineProps({
  proxy: {
    type: Object,
    default: null,
  },
  fetchRows: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["open-chatter"]);

const {
  state,
  version,
  fairPayAtEffectiveDate,
  lapsedReason,
  voidable,
  voidBlockedBy,
  featureDisabled,
  open,
  close,
  openVoid,
  cancelVoid,
  voidVersion,
} = useLapsedPayPlanDrawer({ proxy: props.proxy, fetchRows: props.fetchRows });

defineExpose({ open, close });
</script>

<style scoped>
.lppdc__loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.lppdc__error {
  margin: 16px;
  padding: 12px 16px;
  border: 1px solid #fda29b;
  border-radius: 8px;
  background: #fef3f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.lppdc__field-label {
  margin-bottom: 6px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.lppdc__dialog-copy {
  margin: 0 0 12px;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

.lppdc__dialog-actions {
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}
</style>
